import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiCalendar } from "react-icons/fi";
import styles from "./WidgetAnalytics.module.css";
import AdminHeader from "../../Header/AdminHeader";
import AdminFooter from "../../Footer/AdminFooter";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import { NODE_HOST_URL_API3_v1 } from "../../Dashboard/api/Api";

import StatsRow         from "./components/StatsRow";
import AnalyticsSection from "./components/AnalyticsSection";
import TopPages         from "./components/TopPages";
import TelemetryList    from "./components/TelemetryList";
import LiveWidgetView   from "./components/LiveWidgetView";

const API_URL = `${NODE_HOST_URL_API3_v1}/admin/widget_analytics`;

const DATE_FILTER_MAP = {
  "Today":       "today",
  "Yesterday":   "yesterday",
  "Last 7 Days": "last_7_days",
  "Last 30 Days":"last_30_days",
  "This Month":  "this_month",
  "Last Month":  "last_month",
};

const STAT_META = [
  { key: "total_visitors",  label: "WEBSITE VISITORS", desc: "Unique desktop & mobile users"  },
  { key: "total_sessions",  label: "SESSIONS",         desc: "Total interaction visits"        },
  { key: "page_views",      label: "PAGE VIEWS",       desc: "Total pages loaded with widget"  },
  { key: "widget_opens",    label: "WIDGET OPENS",     desc: "Chat bubbles expanded"           },
  { key: "whatsapp_clicks", label: "WHATSAPP CLICKS",  desc: "Redirects to WhatsApp app"       },
  { key: "chats_started",   label: "CHATS STARTED",    desc: "Visitors who initiated a chat"   },
];

const FUNNEL_COLORS = {
  visitors:        "#4f46e5",
  page_views:      "#818cf8",
  widget_opens:    "#16a34a",
  whatsapp_clicks: "#f59e0b",
  chats_started:   "#0ea5e9",
};

const FUNNEL_LABELS = {
  visitors:        "Visitors",
  page_views:      "Page Views",
  widget_opens:    "Widget Opens",
  whatsapp_clicks: "WhatsApp Clicks",
  chats_started:   "Chats Started",
};

const DEVICE_COLORS = { mobile: "#6366f1", desktop: "#10b981", tablet: "#f59e0b" };

const EVENT_LABELS = {
  whatsapp_click: "Clicked WhatsApp CTA Button",
  widget_open:    "Opened Chat Widget",
  page_view:      "Visited Page",
};

const EVENT_COLORS = {
  whatsapp_click: "#22c55e",
  widget_open:    "#f59e0b",
  page_view:      "#4f46e5",
};

const fmtChange = (c) => (c == null ? null : `${c >= 0 ? "+" : ""}${c}%`);

const fmtShortDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
};

const mergeTrends = (d) => {
  const map = {};
  const add = (arr, key) =>
    (arr || []).forEach(({ date, ...rest }) => {
      if (!map[date]) map[date] = { date: fmtShortDate(date) };
      map[date][key] = Object.values(rest)[0] || 0;
    });
  add(d.visitor_trend,        "visitors");
  add(d.page_view_trend,      "pageViews");
  add(d.widget_open_trend,    "widgetOpens");
  add(d.whatsapp_click_trend, "waClicks");
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 60)  return `${mins} min${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  return `${Math.floor(hrs / 24)} day(s) ago`;
};

const fmtDateTime = (iso) => {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 10)} ${d.toTimeString().slice(0, 5)}`;
};

const getAuth = () => {
  const loginData =
    JSON.parse(localStorage.getItem("login")) ||
    JSON.parse(localStorage.getItem("admin")) ||
    JSON.parse(localStorage.getItem("emp")) ||
    JSON.parse(localStorage.getItem("reseller"));
  return {
    user_id: loginData?.user?.userId,
    token:   loginData?.token,
  };
};

const WidgetAnalytics = () => {
  const [activeView, setActiveView] = useState("analytics");
  const [dateRange, setDateRange] = useState("Last 7 Days");

  // Dashboard
  const [stats,         setStats]         = useState(
    STAT_META.map(({ label, desc }) => ({ label, desc, value: 0, change: null }))
  );
  const [chartData,     setChartData]     = useState([]);
  const [funnelData,    setFunnelData]    = useState([]);
  const [deviceData,    setDeviceData]    = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [dashLoading,   setDashLoading]   = useState(true);

  // Top Pages
  const [topPages,      setTopPages]      = useState([]);
  const [pagesLoading,  setPagesLoading]  = useState(true);
  const [topPagesPage,  setTopPagesPage]  = useState(1);
  const [totalTopPages, setTotalTopPages] = useState(0);

  // Visitors
  const [visitors,        setVisitors]        = useState([]);
  const [visitorsLoading, setVisitorsLoading] = useState(true);
  const [visitorsPage,    setVisitorsPage]    = useState(1);
  const [visitorsPerPage, setVisitorsPerPage] = useState(10);
  const [totalVisitors,   setTotalVisitors]   = useState(0);
  const [visitorsSearch,  setVisitorsSearch]  = useState("");
  const [visitorsStatus,  setVisitorsStatus]  = useState("all");
  const [visitorsDevice,  setVisitorsDevice]  = useState("All");

  const searchTimerRef = useRef(null);

  const handleDateRangeChange = (val) => {
    setDateRange(val);
    setVisitorsPage(1);
    setTopPagesPage(1);
  };

  const handleVisitorsSearchChange = useCallback((val) => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setVisitorsSearch(val);
      setVisitorsPage(1);
    }, 400);
  }, []);

  // ── Dashboard (4 parallel calls) ──────────────────────────────────────────
  useEffect(() => {
    const fetchDashboard = async () => {
      setDashLoading(true);
      const { user_id, token } = getAuth();
      const filter  = DATE_FILTER_MAP[dateRange] || "last_7_days";
      const base    = { user_id, filter };
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [summaryRes, interactionRes, conversionRes, devicesRes] = await Promise.all([
          axios.post(API_URL, { ...base, method: "summary"     }, { headers }),
          axios.post(API_URL, { ...base, method: "interaction" }, { headers }),
          axios.post(API_URL, { ...base, method: "conversion"  }, { headers }),
          axios.post(API_URL, { ...base, method: "devices"     }, { headers }),
        ]);

        // summary → stat cards
        if (summaryRes.data.success) {
          const s = summaryRes.data.data.summary;
          setStats(
            STAT_META.map(({ key, label, desc }) => ({
              label,
              desc,
              value:  s?.[key]?.count  || 0,
              change: fmtChange(s?.[key]?.change),
            }))
          );
        }

        // interaction → line chart
        if (interactionRes.data.success) {
          setChartData(mergeTrends(interactionRes.data.data));
        }

        // conversion → funnel
        if (conversionRes.data.success) {
          const funnel = conversionRes.data.data.conversion_funnel || {};
          setFunnelData(
            Object.entries(funnel).map(([key, val]) => ({
              label: FUNNEL_LABELS[key] || key,
              value: val.count,
              pct:   val.percent,
              color: FUNNEL_COLORS[key] || "#94a3b8",
            }))
          );
        }

        // devices → donut chart
        if (devicesRes.data.success) {
          const dist      = devicesRes.data.data.device_distribution || [];
          const totalSess = dist.reduce((s, x) => s + x.sessions, 0);
          setTotalSessions(totalSess);
          setDeviceData(
            dist.map((x) => ({
              name:  x.device,
              value: totalSess > 0 ? Math.round((x.sessions / totalSess) * 100) : 0,
              color: DEVICE_COLORS[(x.device || "").toLowerCase()] || "#94a3b8",
            }))
          );
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setDashLoading(false);
      }
    };

    fetchDashboard();
  }, [dateRange]);

  // ── Top Pages ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTopPages = async () => {
      setPagesLoading(true);
      const { user_id, token } = getAuth();
      try {
        const res = await axios.post(
          API_URL,
          {
            user_id,
            method: "top_pages",
            filter: DATE_FILTER_MAP[dateRange] || "last_7_days",
            page:   topPagesPage,
            limit:  10,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success) {
          setTopPages(
            (res.data.data || []).map((p) => ({
              url:         p.page_url,
              visitors:    p.visitors,
              pageViews:   p.page_views,
              widgetOpens: p.widget_opens,
              waClicks:    p.whatsapp_clicks,
              conversion:  p.conversion_rate,
            }))
          );
          setTotalTopPages(res.data.pagination?.total_pages || 0);
        }
      } catch (err) {
        console.error("Top pages fetch error:", err);
      } finally {
        setPagesLoading(false);
      }
    };
    fetchTopPages();
  }, [dateRange, topPagesPage]);

  // ── Visitors ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchVisitors = async () => {
      setVisitorsLoading(true);
      const { user_id, token } = getAuth();
      try {
        const body = {
          user_id,
          method: "visitors",
          filter: DATE_FILTER_MAP[dateRange] || "last_7_days",
          page:   visitorsPage,
          limit:  visitorsPerPage,
        };
        if (visitorsSearch)           body.search = visitorsSearch;
        if (visitorsStatus !== "all") body.status = visitorsStatus;
        if (visitorsDevice !== "All") body.device = visitorsDevice;

        const res = await axios.post(API_URL, body, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) {
          setVisitors(
            (res.data.data || []).map((v) => ({
              id:           v.visitor_id,
              mobile:       v.mobile || "–",
              chatStarted:  v.chat_started,
              device:       v.device || "Desktop",
              sessions:     v.sessions,
              pageViews:    v.page_views,
              widgetOpens:  v.widget_opens,
              waClicks:     v.whatsapp_clicks,
              lastSeen:     timeAgo(v.last_seen),
              lastSeenDate: fmtDateTime(v.last_seen),
              status:       v.status === "identified" ? "Identified" : "Anonymous",
              journey:      [],
            }))
          );
          setTotalVisitors(res.data.pagination?.total || 0);
        }
      } catch (err) {
        console.error("Visitors fetch error:", err);
      } finally {
        setVisitorsLoading(false);
      }
    };

    fetchVisitors();
  }, [dateRange, visitorsPage, visitorsPerPage, visitorsSearch, visitorsStatus, visitorsDevice]);

  // ── Timeline (visitor journey) ────────────────────────────────────────────
  const fetchJourney = useCallback(async (visitorId) => {
    const { user_id, token } = getAuth();
    try {
      const res = await axios.post(
        API_URL,
        {
          user_id,
          method:     "timeline",
          visitor_id: visitorId,
          page:        1,
          limit:       50,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const events = (res.data.data || []).map((e) => ({
          event: EVENT_LABELS[e.event] || e.event,
          page:  e.page_url,
          time:  new Date(e.created_at).toLocaleTimeString("en-US", {
            hour:   "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
          color: EVENT_COLORS[e.event] || "#94a3b8",
        }));

        // visitor_info holds lifetime totals (not filtered by date)
        const vi = res.data.visitor_info || {};
        return {
          events,
          visitorInfo: {
            sessions: vi.sessions,
            waClicks: vi.whatsapp_clicks,
          },
        };
      }
    } catch (err) {
      console.error("Timeline fetch error:", err);
    }
    return { events: [], visitorInfo: {} };
  }, []);

  return (
    <div className="wrapper">
      <AdminLeftMenu />
      <div className="main">
        <AdminHeader />

        <div className="container-fluid p-3 p-md-4">

          {/* PAGE HEADER */}
          <div className={`d-flex justify-content-between align-items-center mb-4 mt-3 flex-wrap ${styles.pageHeaderGap}`}>
            <div>
              <div className={styles.pageTitleRow}>
                <h5 className={styles.pageTitle}>
                  {activeView === "analytics" ? "Widget Analytics" : "Live Widget"}
                </h5>
              </div>
            </div>

            <div className={styles.headerControls}>
              <div className={styles.tabGroup}>
                <button
                  className={`${styles.tabBtn} ${activeView === "analytics" ? styles.tabBtnActive : ""}`}
                  onClick={() => setActiveView("analytics")}
                >
                  Widget Analytics
                </button>
                <button
                  className={`${styles.tabBtn} ${activeView === "live" ? styles.tabBtnActive : ""}`}
                  onClick={() => setActiveView("live")}
                >
                  Live Widget
                </button>
              </div>
              {activeView === "analytics" && (
                <div className={styles.dateSelectWrap}>
                  <FiCalendar className={styles.calendarIcon} />
                  <select
                    className={styles.headerSelect}
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <option>Today</option>
                    <option>Yesterday</option>
                    <option>Last 7 Days</option>
                    <option>Last 30 Days</option>
                    <option>This Month</option>
                    <option>Last Month</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {activeView === "analytics" ? (
            <>
              <StatsRow stats={stats} loading={dashLoading} />

              <AnalyticsSection
                chartData={chartData}
                funnelData={funnelData}
                deviceData={deviceData}
                totalSessions={totalSessions}
                loading={dashLoading}
              />

              <TopPages pages={topPages} loading={pagesLoading} />

              <TelemetryList
                visitors={visitors}
                onFetchJourney={fetchJourney}
                loading={visitorsLoading}
                page={visitorsPage}
                perPage={visitorsPerPage}
                totalCount={totalVisitors}
                status={visitorsStatus}
                device={visitorsDevice}
                onPageChange={setVisitorsPage}
                onPerPageChange={(n) => { setVisitorsPerPage(n); setVisitorsPage(1); }}
                onSearchChange={handleVisitorsSearchChange}
                onStatusChange={(s) => { setVisitorsStatus(s); setVisitorsPage(1); }}
                onDeviceChange={(d) => { setVisitorsDevice(d); setVisitorsPage(1); }}
              />
            </>
          ) : (
            <LiveWidgetView />
          )}

        </div>

        <AdminFooter />
      </div>
    </div>
  );
};

export default WidgetAnalytics;
